// Single row in the categories table. Renders as read, edit, or add depending on
// props/state, always through the same row shell — keeps the three states aligned
// by construction instead of relying on separate blocks to agree on spacing.
import { useState } from "react";
import { ConcernCategory } from "../../types/constraint";
import labels from "../../utils/labels";
import constraintStyle from "../../styles/constraint.module.css";
import { ReactComponent as EditIcon } from "../../assets/icons/edit.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/bin.svg";
import IconButtonComponent from "../UI/buttons/IconButton";
import StandardButton from "../UI/buttons/StandardButton";
import InputTextField from "../UI/input/InputTextField";
import { useAppDispatch } from "../../store/hooks";
import ConstraintService from "../../service/constraintService";

const CategoryRow = ({
  category,
  onDelete,
  onAddCancel,
}: {
  // Present for an existing category (read/edit); absent for the add row.
  category?: ConcernCategory;
  onDelete?: () => void;
  onAddCancel?: () => void;
}) => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(category?.name ?? "");
  const [isPending, setIsPending] = useState(false);

  const isFormMode = !category || isEditing;

  const handleCancel = () => {
    if (!category) {
      onAddCancel?.();
      return;
    }
    setName(category.name);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setIsPending(true);
    try {
      if (!category) {
        await ConstraintService.addConcernCategory(name.trim(), dispatch);
        onAddCancel?.();
      } else {
        await ConstraintService.editConcernCategory(
          category.id,
          name.trim(),
          dispatch,
        );
        setIsEditing(false);
      }
    } catch {
      // error notification handled in service
    }
    setIsPending(false);
  };

  return (
    <div
      className={`${constraintStyle.categoryTableRow} ${isFormMode ? constraintStyle.staticRow : ""}`}
      style={{ paddingLeft: "var(--spacing-lg)" }}
    >
      {isFormMode ? (
        <InputTextField
          id={`category-name-${category?.id ?? "add"}`}
          fieldWidth="100%"
          label=""
          value={name}
          placeholder={category?.name ?? labels.categoryNamePlaceholder}
          onInputChange={setName}
        />
      ) : (
        <p>{category.name}</p>
      )}

      {isFormMode ? (
        <div
          className={`${constraintStyle.categoryFormActions} flex justify-between gap-[var(--spacing-base)] items-center`}
        >
          <StandardButton
            variant="TERTIARY"
            onClick={handleCancel}
            ariaLabel={labels.cancel}
          >
            {labels.cancel}
          </StandardButton>
          <StandardButton
            variant="PRIMARY"
            onClick={handleSave}
            ariaLabel={labels.save}
            disable={!name.trim()}
            pending={isPending}
          >
            {labels.save}
          </StandardButton>
        </div>
      ) : (
        <>
          <div className={constraintStyle.actionColumn}>
            <IconButtonComponent
              id={`edit-category-${category.id}`}
              icon={<EditIcon width={24} height={24} />}
              onClick={() => setIsEditing(true)}
              ariaLabel={labels.editCategory}
            />
          </div>
          <div className={constraintStyle.actionColumn}>
            <IconButtonComponent
              id={`delete-category-${category.id}`}
              icon={<DeleteIcon width={24} height={24} />}
              onClick={() => onDelete?.()}
              ariaLabel={labels.delete}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CategoryRow;
